import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import {
  Modal,
  Button,
  ButtonGroup,
  Toggle,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Slider,
} from '@statsbomb/kitbag-components';
import { useReactiveVar } from '@apollo/client';
import classNames from 'classnames/bind';
import {
  uiState,
  cf_FieldPosition,
  cf_FieldPositionEnabled,
} from '../../apollo';
import {
  FilterDynamicValue,
  FilterRoundel,
  FiltersTrack,
  ModalWidth,
  TriggerButtonContainer,
} from './ContextFilters.styles';
import { enableFilter } from './Filters.helper';
import { useIsEligiblePage } from './ContextFilters.hooks';
import { FormField } from '../../styles/global.styles';
import { fieldPositionLabels } from './PositionFilter.constants';

const PositionFilter = ({ handleEnabledToggle }) => {
  const theme = useTheme();
  // modal open/close
  const [st_FieldOpen, setFieldOpen] = useState(false);
  // global state
  const cf_FieldPositionRV = useReactiveVar(cf_FieldPosition);
  const cf_FieldPositionEnabledRV = useReactiveVar(cf_FieldPositionEnabled);
  // local state
  const [st_FieldPosition, setFieldPosition] = useState(cf_FieldPositionRV);

  const handleFieldPosition = (evt, sliderValues) => {
    setFieldPosition([...sliderValues]);
  };

  const handleRedZone = () => {
    setFieldPosition([80, 100]);
    enableFilter(
      cf_FieldPositionEnabledRV,
      cf_FieldPositionEnabled,
      'fieldPosition'
    );
  };

  const handleOpen = () => {
    setFieldOpen(true);
  };

  const handleFieldReset = () => {
    setFieldPosition([0, 100]);
    cf_FieldPositionEnabled(true);

    uiState().contextFilters.fieldPosition.value = [0, 100];
    uiState().contextFilters.fieldPosition.enabled = true;

    sessionStorage.setItem('uiStateLocal', JSON.stringify(uiState()));
  };

  const handleModalClose = () => {
    cf_FieldPosition([...st_FieldPosition]);
    uiState().contextFilters.fieldPosition.value = [...st_FieldPosition];
    cf_FieldPositionEnabled(cf_FieldPositionEnabledRV);

    sessionStorage.setItem('uiStateLocal', JSON.stringify(uiState()));
    setFieldOpen(false);
  };

  const filterCount = [cf_FieldPositionEnabledRV].filter(Boolean).length;
  const isEligiblePage = useIsEligiblePage('FIELD_POSITION');
  const isEnabled = filterCount > 0 && isEligiblePage;

  const positionYards = (pos, flip) => {
    let yds = parseInt(pos, 10);

    let prefix = '';
    if (yds < 50) {
      prefix = 'own ';
    } else if (yds > 50) {
      prefix = 'opp ';
      yds -= 100;
    }

    if (flip) {
      return `${prefix}${Math.abs(yds)}`;
    }
    return `${Math.abs(yds)} ${prefix}`;
  };

  return (
    <>
      <TriggerButtonContainer>
        <Button
          variant={isEnabled ? 'primary' : 'secondary'}
          size="small"
          isDisabled={!isEligiblePage}
          onClick={handleOpen}
        >
          Field Position
        </Button>
        {isEnabled && <FilterRoundel>{filterCount}</FilterRoundel>}
      </TriggerButtonContainer>

      <Modal onCancel={handleModalClose} isOpen={st_FieldOpen}>
        <ModalWidth $minWidth="570px">
          <ModalHeader>Field Position</ModalHeader>
          <ModalBody>
            <FormField $marginBottom="0.5rem">
              <Toggle
                id="field-position-cf-toggle"
                label="Field Position"
                onChange={() =>
                  handleEnabledToggle(
                    !cf_FieldPositionEnabledRV,
                    cf_FieldPositionEnabled,
                    'fieldPosition',
                    {
                      enabled: !cf_FieldPositionEnabledRV,
                    }
                  )
                }
                checked={cf_FieldPositionEnabledRV}
                size="small"
              />
              <FilterDynamicValue
                className={classNames({
                  grey: !cf_FieldPositionEnabledRV,
                })}
              >
                {st_FieldPosition &&
                  `${positionYards(
                    st_FieldPosition[0],
                    true
                  )} → ${positionYards(st_FieldPosition[1], false)}`}
              </FilterDynamicValue>
            </FormField>
            <FormField $marginBottom="0">
              <Slider
                id="field-position-slider"
                labels={fieldPositionLabels}
                min={0}
                max={100}
                onChange={handleFieldPosition}
                isDisabled={!cf_FieldPositionEnabledRV}
                isPushable
                value={[...st_FieldPosition]}
                version={2}
              />
              <FiltersTrack
                className="labels"
                $opacity={cf_FieldPositionEnabledRV ? 1 : 0.35}
              >
                <div>Own</div>
                <div>Opp</div>
              </FiltersTrack>
              <div style={{ marginBottom: theme.sizing.small }}>
                <ButtonGroup>
                  <Button
                    size="small"
                    onClick={handleRedZone}
                    variant="secondary"
                    isDisabled={!cf_FieldPositionEnabledRV}
                  >
                    Redzone
                  </Button>
                </ButtonGroup>
              </div>
            </FormField>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={handleFieldReset}>
              Reset
            </Button>
            <Button onClick={handleModalClose}>Save</Button>
          </ModalFooter>
        </ModalWidth>
      </Modal>
    </>
  );
};

PositionFilter.propTypes = {
  handleEnabledToggle: PropTypes.func.isRequired,
};

export default PositionFilter;
